import { useMemo } from 'react';
import type {
    DataTableFilter,
    DataTableFilterMultiple,
    FilterOption,
    MoreMenuCategory
} from '@odo/shared-ui';
import { usePracticeOptions } from '../../../hooks/useBilling';
import type { DmInboxStatus } from '../../../services/dmNotifications';
import { practiceOptionsFromList } from '../../../utils/practiceFilterOptions';
import { DEFAULT_INBOX_STATUS, parseInboxStatus } from './inboxStatus';
import { INBOX_TAG_OPTIONS } from './inboxTags';

type InboxFilterSpec = Pick<
    DataTableFilterMultiple,
    'key' | 'label' | 'options' | 'value' | 'onChange' | 'searchable'
>;

// Every Monitoring inbox filter is a right-aligned multi-select pill.
function inboxFilter(spec: InboxFilterSpec): DataTableFilterMultiple {
    return { ...spec, multiple: true, align: 'right' };
}

function usePracticeFilterOptions(): FilterOption[] {
    const { practices } = usePracticeOptions();
    return useMemo(() => practiceOptionsFromList(practices), [practices]);
}

interface BarFilterSource {
    assigneeOptions: DataTableFilterMultiple['options'];
    assignees: string[];
    practiceIds: string[];
    setFilter: (name: 'assignees' | 'practiceIds', value: string[]) => void;
}

// The pills both inboxes keep on the bar, in the work queue's order; the rest sit in More.
export function useInboxBarFilters({
    assigneeOptions,
    assignees,
    practiceIds,
    setFilter
}: BarFilterSource): DataTableFilter[] {
    const practiceOptions = usePracticeFilterOptions();
    return [
        inboxFilter({
            key: 'assignees',
            label: 'Assignee',
            searchable: true,
            options: assigneeOptions,
            value: assignees,
            onChange: (value) => setFilter('assignees', value)
        }),
        inboxFilter({
            key: 'practiceIds',
            label: 'Practice',
            searchable: true,
            options: practiceOptions,
            value: practiceIds,
            onChange: (value) => setFilter('practiceIds', value)
        })
    ];
}

const STATUS_OPTIONS: readonly FilterOption[] = [
    { value: 'open', label: 'Open' },
    { value: 'closed', label: 'Closed', sublabel: 'Handled in the last 24 hours' }
];

export function statusCategory(
    status: DmInboxStatus,
    setStatus: (status: DmInboxStatus) => void
): MoreMenuCategory {
    return {
        key: 'status',
        label: 'Status',
        options: STATUS_OPTIONS,
        selectedOptions: [status],
        onChange: ([value]) => setStatus(parseInboxStatus(value ?? null)),
        selection: 'single',
        defaultValue: DEFAULT_INBOX_STATUS
    };
}

export function tagsCategory(
    value: string[],
    onChange: (value: string[]) => void
): MoreMenuCategory {
    return {
        key: 'tags',
        label: 'Tags',
        options: INBOX_TAG_OPTIONS,
        selectedOptions: value,
        onChange
    };
}
